// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import difference from 'lodash/difference';

import {SwitchEl} from 'common/components/switch';
import {TooltipEl} from 'common/components/tooltip';
import {ToggleEl} from 'common/components/toggle';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {baseComparator} from 'common/util';
import {OrderBy} from 'common/util/array';
import {SvgIconEl} from 'common/components/svg-icon';

import {trackClick} from '../table-cell-mixins';
import type {ExpandedRow} from './index';

export function template(this: ExpandedRow) {
  const {t} = this.getContext(`i18n`);

  const RowContents = () => {
    const event = this.helpers.getFilteredEvent();
    if (this.helpers.isJsonView()) {
      return <pre sel='.expanded-row-json'>{JSON.stringify(event, null, 2)}</pre>;
    }
    const properties = {...event.properties};
    const pinnedKeys = this.helpers.getPinnedProperties();

    // For UX reasons, maintain an empty value if the property isn't set on this event before sorting
    if (pinnedKeys.length) {
      const missingKeys = difference(pinnedKeys, Object.keys(properties));
      missingKeys.forEach((missingKey) => {
        properties[missingKey] = ``;
      });
    }

    // Sort by property name
    let sortedAvailablePropertyKeys = Object.keys(properties).sort(
      baseComparator({order: OrderBy.Ascending, transform: (item: string) => item.toLowerCase()}),
    );

    // If there are pinned keys, re-sort those to the top.
    if (pinnedKeys.length) {
      sortedAvailablePropertyKeys = sortedAvailablePropertyKeys.sort((a, b) => {
        const aPinned = pinnedKeys.includes(a);
        const bPinned = pinnedKeys.includes(b);
        if (aPinned && !bPinned) {
          return -1;
        } else if (bPinned) {
          return 1;
        }
        return 0;
      });
    }
    const numProps = sortedAvailablePropertyKeys.length;
    const itemsPerColumn = Math.ceil(numProps / 3);

    return (
      <div sel='.expanded-row-grid'>
        {sortedAvailablePropertyKeys.map((key, index) => {
          const col = Math.floor(index / itemsPerColumn) + 1;
          const isLastItemInCol = index === numProps - 1 || (index + 1) % itemsPerColumn === 0;
          const value = properties[key].formattedValue;
          const href = properties[key].href;
          const raw = properties[key].raw;
          return (
            <div
              sel='.expanded-row-grid-item'
              class={{
                [`expanded-row-grid-item-col-${col}`]: true,
                'expanded-row-last-item-in-col': isLastItemInCol,
              }}
            >
              {this.attr(`allow-pinning`) && (
                <div
                  sel='.expanded-row-pin'
                  class={{'expanded-row-pinned': this.helpers.isPinned(key)}}
                  on={{
                    click: () => {
                      this.helpers.onPinClick(key);
                    },
                  }}
                >
                  <SvgIconEl attrs={{icon: `pin`}} />
                </div>
              )}
              <div
                sel='.expanded-row-label'
                class={{'expanded-row-truncate': key?.length > 20}}
                attrs={{title: key}}
              >{`${key}:`}</div>
              <div sel='.expanded-row-item-text'>
                {href ? (
                  <a
                    attrs={{href, target: `_blank`, rel: `noopener noreferrer`}}
                    on={{click: (ev) => trackClick(ev, href, key)}}
                  >
                    {value}
                  </a>
                ) : (
                  value
                )}
                <TooltipEl>{typeof raw === `string` ? raw : value}</TooltipEl>
              </div>
            </div>
          );
        })}
      </div>
    );
  };
  return (
    <div sel='.expanded-row'>
      <div sel='.expanded-row-header'>
        {!this.attr(`hide-property-key-filter`) && (
          <ToggleEl
            attrs={{
              selected: this.attr(`property-key-filter`),
              size: ToggleSize.Medium,
              theme: this.attr(`toggletheme`) as ToggleTheme,
              options: this.helpers.getPropertyKeyFilter(),
            }}
            on={{
              select: this.helpers.onSelectPropertyKeyFilter,
            }}
          />
        )}
        {!this.attr(`hide-view-format-switch`) && (
          <div sel='.expanded-row-switch'>
            <SwitchEl attrs={{on: this.helpers.isJsonView()}} on={{change: this.helpers.onChangeViewFormat}} />
            <div sel='.expanded-row-switch-label'>{t(`events.json-mode`)}</div>
          </div>
        )}
      </div>
      <RowContents />
    </div>
  );
}
